import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type {
  ErrorAlert,
  Persona,
  PersonaCreateInput,
  PersonaDeleteResult,
  PersonaUpdateInput,
} from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UsePersonaMutationsResult {
  create: (input: PersonaCreateInput) => Promise<Persona>;
  isCreating: boolean;
  update: (input: PersonaUpdateInput) => Promise<Persona>;
  isUpdating: boolean;
  remove: (personaId: string) => Promise<void>;
  isDeleting: boolean;
}

/** F16 create/update/delete — invalidates every `persona.list` scope on success. */
export function usePersonaMutations(): UsePersonaMutationsResult {
  const { wsClient } = useWs();
  const queryClient = useQueryClient();

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['personas', 'list'] });
  }

  const createMutation = useMutation<Persona, ErrorAlert, PersonaCreateInput>({
    mutationFn: (input) => requestTyped(wsClient, 'persona.create', input),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation<Persona, ErrorAlert, PersonaUpdateInput>({
    mutationFn: (input) => requestTyped(wsClient, 'persona.update', input),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation<PersonaDeleteResult, ErrorAlert, string>({
    mutationFn: (personaId) => requestTyped(wsClient, 'persona.delete', { personaId }),
    onSuccess: invalidate,
  });

  return {
    create: (input) => createMutation.mutateAsync(input),
    isCreating: createMutation.isPending,
    update: (input) => updateMutation.mutateAsync(input),
    isUpdating: updateMutation.isPending,
    remove: async (personaId) => void (await deleteMutation.mutateAsync(personaId)),
    isDeleting: deleteMutation.isPending,
  };
}
